import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { QRCodeSVG } from "qrcode.react";
import { api, fmtMoney, formatApiErrorDetail } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { Plus, QrCode, Receipt, ArrowsClockwise, Prohibit, CheckCircle } from "@phosphor-icons/react";

const STATUS_COLORS = {
  available: { bg: "bg-[#81B29A]/20", text: "text-[#3f6953]", border: "border-[#81B29A]" },
  occupied: { bg: "bg-[var(--brand)]/15", text: "text-[var(--brand)]", border: "border-[var(--brand)]" },
  reserved: { bg: "bg-[var(--brand-3)]/30", text: "text-[#85622d]", border: "border-[var(--brand-3)]" },
  cleaning: { bg: "bg-[var(--status-served)]/25", text: "text-[#5d5167]", border: "border-[var(--status-served)]" },
};

const NEXT_STATUS = ["available", "reserved", "cleaning"];

export default function Tables() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [tables, setTables] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ number: "", capacity: 4, section: "Main", status: "available" });
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const [qr, setQr] = useState(null);
  const [busyQr, setBusyQr] = useState(false);

  const canEdit = ["admin", "manager"].includes(user?.role);
  const canManageQr = canEdit;

  const load = () => api.get("/tables").then((r) => setTables(r.data));
  useEffect(() => { load(); }, []);

  const openTable = async (t) => {
    setSelected(t);
    setDetail(null);
    setQr(null);
    try {
      const { data } = await api.get(`/tables/${t.id}/order`);
      setDetail(data);
    } catch {
      toast.error("Could not load table order");
    }
    if (canManageQr) {
      try {
        const { data } = await api.get(`/tables/${t.id}/qr`);
        setQr(data);
      } catch {
        /* ignore */
      }
    }
  };

  // Deep-link from staff QR / notifications: /app/tables?open=<tableId>
  useEffect(() => {
    const openId = searchParams.get("open");
    if (!openId || tables.length === 0) return;
    const t = tables.find((x) => x.id === openId);
    if (t) {
      openTable(t);
      searchParams.delete("open");
      setSearchParams(searchParams, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tables, searchParams]);

  const create = async () => {
    try {
      await api.post("/tables", form);
      toast.success("Table added (QR generated)");
      setOpen(false);
      setForm({ number: "", capacity: 4, section: "Main", status: "available" });
      load();
    } catch {
      toast.error("Failed to add table");
    }
  };

  const setStatus = async (id, status) => {
    try {
      await api.patch(`/tables/${id}/status`, { status });
      load();
      if (selected?.id === id) {
        const t = { ...selected, status };
        setSelected(t);
      }
    } catch {
      toast.error("Cannot update status");
    }
  };

  const regenerateQr = async () => {
    if (!selected) return;
    setBusyQr(true);
    try {
      const { data } = await api.post(`/tables/${selected.id}/qr/regenerate`);
      setQr(data);
      toast.success("QR rotated — old codes are invalid");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed");
    } finally {
      setBusyQr(false);
    }
  };

  const toggleQr = async (enable) => {
    if (!selected) return;
    setBusyQr(true);
    try {
      if (enable) {
        const { data } = await api.post(`/tables/${selected.id}/qr/enable`);
        setQr(data);
        toast.success("QR enabled");
      } else {
        await api.post(`/tables/${selected.id}/qr/disable`);
        setQr((q) => (q ? { ...q, qr_active: false } : q));
        toast.success("QR disabled");
      }
      load();
    } catch {
      toast.error("Failed to update QR");
    } finally {
      setBusyQr(false);
    }
  };

  const guestUrl = useMemo(() => {
    if (!qr?.guest_path) return "";
    return `${window.location.origin}${qr.guest_path}`;
  }, [qr]);

  const sections = Array.from(new Set(tables.map((t) => t.section)));

  return (
    <>
      <PageHeader
        eyebrow="Floor plan"
        title="Tables"
        subtitle="Each table has a secure QR (admin/manager). Only staff can place table orders. Tap a table to view its order or manage QR."
        actions={canEdit && (
          <Button onClick={() => setOpen(true)} className="btn-primary" data-testid="tables-add-btn">
            <Plus size={16} weight="bold" /> Add table
          </Button>
        )}
      />

      <div className="space-y-6">
        {sections.map((sec) => (
          <div key={sec}>
            <div className="label-eyebrow mb-3">{sec}</div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 stagger" data-testid={`tables-section-${sec.toLowerCase()}`}>
              {tables.filter((t) => t.section === sec).map((t) => {
                const c = STATUS_COLORS[t.status] || STATUS_COLORS.available;
                return (
                  <button
                    key={t.id}
                    type="button"
                    onClick={() => openTable(t)}
                    className={`tile p-4 text-left border-2 ${c.border} ${c.bg}`}
                    data-testid={`table-${t.number}`}
                  >
                    <div className="flex justify-between items-start">
                      <div className="font-display font-extrabold text-2xl">{t.number}</div>
                      <div className={`text-[10px] uppercase tracking-wider font-bold ${c.text}`}>{t.status}</div>
                    </div>
                    <div className="text-xs text-[var(--text-secondary)] mt-1">{t.capacity} seats</div>
                    {t.has_qr && (
                      <div className="mt-2 flex items-center gap-1 text-[10px] uppercase tracking-wider text-[var(--text-secondary)]">
                        <QrCode size={12} /> QR active
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        ))}
        {tables.length === 0 && (
          <div className="bento p-8 text-center text-sm text-[var(--text-secondary)]">No tables yet.</div>
        )}
      </div>

      {/* Table detail */}
      <Dialog open={!!selected} onOpenChange={(v) => { if (!v) setSelected(null); }}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="table-detail-dialog">
          <DialogHeader>
            <DialogTitle>Table {selected?.number}</DialogTitle>
          </DialogHeader>

          {selected && (
            <div className="space-y-5">
              <div className="flex flex-wrap gap-2 items-center">
                <span className={`text-xs font-bold uppercase tracking-wider px-2 py-1 rounded ${STATUS_COLORS[selected.status]?.bg}`}>
                  {selected.status}
                </span>
                <span className="text-xs text-[var(--text-secondary)]">{selected.capacity} seats · {selected.section}</span>
              </div>

              {selected.status !== "occupied" && (
                <div className="flex flex-wrap gap-2">
                  {NEXT_STATUS.filter((s) => s !== selected.status).map((s) => (
                    <Button key={s} size="sm" variant="outline" onClick={() => setStatus(selected.id, s)}>
                      Mark {s}
                    </Button>
                  ))}
                </div>
              )}

              <div>
                <div className="label-eyebrow mb-2 flex items-center gap-2">
                  <Receipt size={14} /> Current order
                </div>
                {!detail?.order ? (
                  <div className="text-sm text-[var(--text-secondary)] bento p-4">
                    No active order on this table.
                    <div className="mt-3">
                      <Button
                        className="btn-primary"
                        size="sm"
                        onClick={() => nav(`/app/pos?table=${selected.id}`)}
                        data-testid="table-open-pos"
                      >
                        Take order on POS
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="bento p-4 space-y-2" data-testid="table-current-order">
                    <div className="flex justify-between text-sm">
                      <span className="font-semibold">{detail.order.order_number}</span>
                      <span className="uppercase text-xs tracking-wider">{detail.order.status}</span>
                    </div>
                    <ul className="text-sm space-y-1">
                      {detail.order.items?.map((it, i) => (
                        <li key={i} className="flex justify-between gap-2">
                          <span>{it.quantity}× {it.name}</span>
                          <span className="text-[var(--text-secondary)]">{fmtMoney(it.line_total)}</span>
                        </li>
                      ))}
                    </ul>
                    <div className="flex justify-between font-semibold pt-2 border-t border-[#EAE7E0]">
                      <span>Total</span>
                      <span>{fmtMoney(detail.order.total)}</span>
                    </div>
                    <div className="flex gap-2 pt-2">
                      <Button size="sm" variant="outline" asChild>
                        <Link to="/app/orders">Orders</Link>
                      </Button>
                      <Button size="sm" className="btn-primary" onClick={() => nav(`/app/pos?table=${selected.id}`)}>
                        Add items (POS)
                      </Button>
                    </div>
                  </div>
                )}
              </div>

              {canManageQr && (
                <div data-testid="table-qr-panel">
                  <div className="label-eyebrow mb-2 flex items-center gap-2">
                    <QrCode size={14} /> Table QR (secure)
                  </div>
                  <p className="text-xs text-[var(--text-secondary)] mb-3">
                    Guests scan this to view the menu and call a waiter. Ordering for this table is staff-only.
                    Regenerating invalidates printed codes.
                  </p>
                  {qr?.qr_token && qr.qr_active ? (
                    <div className="flex flex-col sm:flex-row gap-4 items-start">
                      <div className="bg-white p-3 rounded-lg border border-[#EAE7E0]">
                        <QRCodeSVG value={guestUrl} size={148} level="M" includeMargin />
                      </div>
                      <div className="flex-1 space-y-2 min-w-0">
                        <div className="text-xs break-all bg-[var(--bg-secondary)] p-2 rounded font-mono">{guestUrl}</div>
                        <div className="flex flex-wrap gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => {
                              navigator.clipboard.writeText(guestUrl);
                              toast.success("Link copied");
                            }}
                          >
                            Copy link
                          </Button>
                          <Button size="sm" variant="outline" disabled={busyQr} onClick={regenerateQr}>
                            <ArrowsClockwise size={14} /> Regenerate
                          </Button>
                          <Button size="sm" variant="outline" disabled={busyQr} onClick={() => toggleQr(false)}>
                            <Prohibit size={14} /> Disable
                          </Button>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <Button size="sm" className="btn-primary" disabled={busyQr} onClick={() => toggleQr(true)}>
                      <CheckCircle size={14} /> Generate / enable QR
                    </Button>
                  )}
                </div>
              )}
            </div>
          )}

          <DialogFooter>
            <Button variant="ghost" onClick={() => setSelected(null)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="add-table-dialog">
          <DialogHeader><DialogTitle>Add table</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="label-eyebrow">Number</Label>
              <Input value={form.number} onChange={(e) => setForm({ ...form, number: e.target.value })} className="mt-1" data-testid="table-number-input" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="label-eyebrow">Capacity</Label>
                <Input type="number" value={form.capacity} onChange={(e) => setForm({ ...form, capacity: parseInt(e.target.value || 0) })} className="mt-1" data-testid="table-capacity-input" />
              </div>
              <div>
                <Label className="label-eyebrow">Section</Label>
                <Input value={form.section} onChange={(e) => setForm({ ...form, section: e.target.value })} className="mt-1" data-testid="table-section-input" />
              </div>
            </div>
            <div>
              <Label className="label-eyebrow">Status</Label>
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger className="mt-1" data-testid="table-status-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="available">Available</SelectItem>
                  <SelectItem value="reserved">Reserved</SelectItem>
                  <SelectItem value="cleaning">Cleaning</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={create} className="btn-primary" data-testid="table-create-submit">Add table</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
